import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Eye, EyeOff, Loader2, ArrowRight, Check, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import AuthShell from "../components/AuthShell";
import GoogleSignInButton from "../components/GoogleSignInButton";

/** Canonical plan definitions — kept in sync with backend billing lookup keys. */
const PLAN_META = {
  essentiel_monthly: { label: "Essentiel", price: "34,99 € HT/mois", tier: "essentiel", interval: "monthly" },
  essentiel_yearly:  { label: "Essentiel", price: "349,90 € HT/an",  tier: "essentiel", interval: "yearly"  },
  pro_monthly:       { label: "Pro",       price: "49,99 € HT/mois", tier: "pro",       interval: "monthly" },
  pro_yearly:        { label: "Pro",       price: "499,90 € HT/an",  tier: "pro",       interval: "yearly"  },
  integral_monthly:  { label: "Intégral",  price: "69,99 € HT/mois", tier: "integral",  interval: "monthly" },
  integral_yearly:   { label: "Intégral",  price: "699,90 € HT/an",  tier: "integral",  interval: "yearly"  },
};

/** Accept both new canonical keys and legacy ones (monthly/yearly). Default: pro_monthly. */
function normalizePlan(raw) {
  if (raw && PLAN_META[raw]) return raw;
  if (raw === "yearly") return "pro_yearly";
  if (raw === "monthly") return "pro_monthly";
  return "pro_monthly";
}

function apiError(e) {
  const d = e?.response?.data?.detail;
  if (!d) return "Une erreur est survenue. Réessayez.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
  return String(d);
}

export default function Signup() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [search] = useSearchParams();
  const initialPlan = useMemo(() => normalizePlan(search.get("plan")), [search]);
  const [f, setF] = useState({
    first_name: "", last_name: "", establishment_name: "",
    email: "", password: "", confirm: "", tos: false, plan: initialPlan,
  });
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const canSubmit = f.first_name && f.last_name && f.establishment_name && f.email &&
                    f.password && f.confirm && f.tos && f.password === f.confirm;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (f.password.length < 8) { setErr("Le mot de passe doit faire au moins 8 caractères."); return; }
    if (f.password !== f.confirm) { setErr("Les mots de passe ne correspondent pas."); return; }
    setErr(""); setLoading(true);
    try {
      // Persist chosen plan for the settings page to pre-select after login.
      try { sessionStorage.setItem("rs_selected_plan", f.plan); } catch { /* ignore */ }
      await register({
        first_name: f.first_name.trim(),
        last_name: f.last_name.trim(),
        establishment_name: f.establishment_name.trim(),
        email: f.email.trim(),
        password: f.password,
        plan: f.plan,
      });
      nav("/bienvenue", { replace: true });
    } catch (ex) {
      setErr(apiError(ex));
    } finally { setLoading(false); }
  };

  const meta = PLAN_META[f.plan] || PLAN_META.pro_monthly;

  return (
    <AuthShell dataTestId="signup-page" title="Créer votre compte" subtitle="14 jours d'essai gratuit · Sans carte bancaire.">
      <form onSubmit={onSubmit} className="space-y-3.5" data-testid="signup-form">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Prénom" testid="signup-first-name"
            value={f.first_name} onChange={(v) => set("first_name", v)} required autoComplete="given-name" />
          <Field label="Nom" testid="signup-last-name"
            value={f.last_name} onChange={(v) => set("last_name", v)} required autoComplete="family-name" />
        </div>
        <Field label="Nom de l'établissement" testid="signup-establishment"
          value={f.establishment_name} onChange={(v) => set("establishment_name", v)} required autoComplete="organization" />
        <Field label="Adresse e-mail" testid="signup-email"
          type="email" value={f.email} onChange={(v) => set("email", v)} required autoComplete="email" />

        <div>
          <label className="block text-[12px] font-semibold text-[#111111] mb-1.5">Mot de passe (min. 8 caractères)</label>
          <div className="relative">
            <input type={showPw ? "text" : "password"} required minLength={8} value={f.password} onChange={(e) => set("password", e.target.value)}
              autoComplete="new-password"
              data-testid="signup-password"
              className="w-full h-11 pl-3 pr-10 rounded-md border border-[#CDD7E3] bg-white text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2878FF]/25 focus:border-[#2878FF]" />
            <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#5E6875]">
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <Field label="Confirmer le mot de passe" testid="signup-confirm"
          type={showPw ? "text" : "password"} value={f.confirm} onChange={(v) => set("confirm", v)} required minLength={8} autoComplete="new-password" />

        <label className="flex items-start gap-2 text-[12.5px] text-[#111111] cursor-pointer select-none pt-1">
          <input type="checkbox" checked={f.tos} onChange={(e) => set("tos", e.target.checked)} data-testid="signup-tos" className="mt-0.5 accent-[#1769E0]" />
          <span>J'accepte les <Link to="/cgu" className="underline text-[#1769E0]">CGU</Link> et la <Link to="/confidentialite" className="underline text-[#1769E0]">politique de confidentialité</Link>.</span>
        </label>

        <div className="rounded-md border border-[#FFD166] bg-[#FFF0D2] p-3 space-y-2.5" data-testid="signup-plan-picker">
          <div className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 text-[12.5px] text-[#7A4C0B] font-medium">
              <Check className="w-3.5 h-3.5" /> Formule choisie
            </span>
            <span className="text-[12.5px] font-semibold text-[#111111]" data-testid="signup-plan">
              {meta.label} — {meta.price}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-1.5 pt-4">
            {["essentiel", "pro", "integral"].map((tier) => {
              const selected = meta.tier === tier;
              const label = PLAN_META[`${tier}_monthly`].label;
              const isPro = tier === "pro";
              const isIntegral = tier === "integral";
              // Base class : hauteur augmentée pour laisser respirer le halo Intégral et le badge Pro
              const base = "relative h-10 rounded-md text-[11.5px] font-semibold transition-colors overflow-visible";
              let variant;
              if (isIntegral) {
                variant = selected
                  ? "bg-[#1769E0] text-white border border-[#1769E0] ring-2 ring-[#2878FF]/60 ring-offset-2 ring-offset-[#FFF0D2] shadow-[0_10px_28px_rgba(23,105,224,0.55)] overflow-hidden"
                  : "bg-gradient-to-br from-[#FF9F1C] to-[#F58F06] text-[#0B1F33] border border-[#F58F06] ring-2 ring-[#FFD166]/60 ring-offset-2 ring-offset-[#FFF0D2] shadow-[0_8px_24px_rgba(255,159,28,0.40)] hover:shadow-[0_10px_28px_rgba(255,159,28,0.55)] overflow-hidden";
              } else if (isPro) {
                variant = selected
                  ? "bg-[#1769E0] text-white border-2 border-[#FF9F1C] shadow-[0_6px_16px_rgba(23,105,224,0.35)]"
                  : "bg-white text-[#0B1F33] border-2 border-[#FF9F1C] shadow-[0_4px_12px_rgba(255,159,28,0.15)] hover:bg-[#FFF6DA]";
              } else {
                variant = selected
                  ? "bg-[#1769E0] text-white shadow-[0_6px_16px_rgba(23,105,224,0.30)]"
                  : "bg-white text-[#0B1F33] border border-[#E7C77A] hover:bg-[#FFF6DA]";
              }
              return (
                <button
                  key={tier}
                  type="button"
                  data-testid={`signup-plan-tier-${tier}`}
                  onClick={() => set("plan", `${tier}_${meta.interval}`)}
                  className={`${base} ${variant}`}
                >
                  {isIntegral && (
                    <>
                      <motion.span
                        aria-hidden
                        className="pointer-events-none absolute -inset-1 rounded-md opacity-60 blur-lg"
                        style={{
                          background: selected
                            ? "conic-gradient(from 0deg, rgba(40,120,255,0.75), rgba(120,180,255,0.35), rgba(40,120,255,0.75), rgba(120,180,255,0.35), rgba(40,120,255,0.75))"
                            : "conic-gradient(from 0deg, rgba(255,159,28,0.65), rgba(255,209,102,0.30), rgba(255,159,28,0.65), rgba(255,209,102,0.30), rgba(255,159,28,0.65))",
                        }}
                        initial={{ rotate: 0 }}
                        animate={{ rotate: 360 }}
                        transition={{ duration: 12, ease: "linear", repeat: Infinity }}
                      />
                      <motion.span
                        aria-hidden
                        className="absolute inset-y-0 w-1/3 pointer-events-none"
                        style={{
                          background:
                            "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.80) 50%, transparent 100%)",
                        }}
                        initial={{ x: "-120%" }}
                        animate={{ x: "220%" }}
                        transition={{ duration: 2.2, ease: "easeInOut", repeat: Infinity, repeatDelay: 1.2 }}
                      />
                    </>
                  )}
                  {isPro && (
                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 inline-flex items-center px-1.5 py-[1px] rounded-full bg-[#FF9F1C] text-[#0B1F33] text-[8.5px] font-extrabold uppercase tracking-wide shadow-sm whitespace-nowrap z-20">
                      Le plus choisi
                    </span>
                  )}
                  {isIntegral && (
                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 inline-flex items-center gap-0.5 px-1.5 py-[1px] rounded-full bg-gradient-to-r from-[#F58F06] via-[#FFB84D] to-[#F58F06] text-[#0B1F33] text-[8.5px] font-extrabold uppercase tracking-wide shadow-sm whitespace-nowrap z-20 overflow-hidden">
                      <Sparkles className="w-2.5 h-2.5" strokeWidth={2.8} />
                      Meilleure offre
                      <motion.span
                        aria-hidden
                        className="absolute inset-y-0 w-1/2 pointer-events-none"
                        style={{
                          background:
                            "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.75) 50%, transparent 100%)",
                        }}
                        initial={{ x: "-120%" }}
                        animate={{ x: "220%" }}
                        transition={{ duration: 2.2, ease: "easeInOut", repeat: Infinity, repeatDelay: 1.2 }}
                      />
                    </span>
                  )}
                  <span className="relative z-10 inline-flex items-center justify-center gap-1">
                    {isIntegral && <Sparkles className="w-3 h-3" strokeWidth={2.8} />}
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
          <div
            role="tablist"
            aria-label="Fréquence de facturation"
            className="relative inline-flex w-full items-center rounded-full bg-white border border-[#E7C77A] p-0.5"
          >
            {[
              { k: "monthly", txt: "Mensuel" },
              { k: "yearly",  txt: "Annuel"  },
            ].map((i) => {
              const active = meta.interval === i.k;
              return (
                <button
                  key={i.k}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  data-testid={`signup-plan-interval-${i.k}`}
                  onClick={() => set("plan", `${meta.tier}_${i.k}`)}
                  className={`flex-1 h-7 text-[11.5px] font-semibold rounded-full inline-flex items-center justify-center gap-1.5 transition-colors ${
                    active ? "bg-[#0B1F33] text-white" : "text-[#0B1F33]"
                  }`}
                >
                  {i.txt}
                  {i.k === "yearly" && (
                    <span className={`inline-flex items-center px-1.5 rounded-full text-[9px] font-extrabold uppercase tracking-wide ${
                      active ? "bg-[#FF9F1C] text-[#0B1F33]" : "bg-[#FFF0D2] text-[#7A4C0B] border border-[#E7C77A]"
                    }`}>
                      2 mois offerts
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {err && <div className="text-[12.5px] text-[#7C2A21] bg-[#FDECEC] border border-[#F5C7C7] rounded-md px-3 py-2" data-testid="signup-error">{err}</div>}

        <button type="submit" disabled={loading || !canSubmit} data-testid="signup-submit"
          className="w-full h-11 rounded-md text-[#111111] text-[14px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
          style={{ background: "#FF9F1C" }}
          onMouseOver={(e) => e.currentTarget.style.background = "#F58F06"}
          onMouseOut={(e) => e.currentTarget.style.background = "#FF9F1C"}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          Démarrer mon essai gratuit
        </button>
      </form>

      <div className="mt-4 flex items-center gap-3">
        <div className="flex-1 h-px bg-[#CDD7E3]" aria-hidden />
        <span className="text-[11px] uppercase tracking-widest font-bold text-[#5E6875]">ou</span>
        <div className="flex-1 h-px bg-[#CDD7E3]" aria-hidden />
      </div>
      <div className="mt-4">
        <GoogleSignInButton label="S'inscrire avec Google" testid="google-signup-btn" />
      </div>

      <div className="mt-5 text-[13px] text-[#5E6875] text-center">
        Déjà un compte ?{" "}
        <Link to="/connexion" data-testid="login-link" className="font-semibold text-[#1769E0] hover:text-[#0B1F33]">Se connecter</Link>
      </div>
    </AuthShell>
  );
}

function Field({ label, value, onChange, type = "text", required, minLength, autoComplete, testid }) {
  return (
    <div>
      <label className="block text-[12px] font-semibold text-[#111111] mb-1.5">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        required={required} minLength={minLength} autoComplete={autoComplete} data-testid={testid}
        className="w-full h-11 px-3 rounded-md border border-[#CDD7E3] bg-white text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2878FF]/25 focus:border-[#2878FF]" />
    </div>
  );
}
